import SaveIcon from '@mui/icons-material/Save';
import BugReportIcon from '@mui/icons-material/BugReport';
import { Box, Step, StepButton, Stepper, Button as MuiButton } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import React, { useState } from 'react';
import { ButtonProps, FormDataConsumer, SimpleForm, Toolbar, useNotify, useRecordContext } from 'react-admin';
import WizardButton from './wizardButton';

interface FormStep {
  label: string;
  component: React.ReactElement;
  validate?: ((formData: any) => { error: boolean, message?: string }) | ((formData: any) => Promise<{ error: boolean, message?: string }>),
  onNextFunction?: (formData: any, nextStep: () => void) => void;
  disableToolbarButton?: boolean | null;
  disableToolbar?: boolean | null;
  disableStep?: (data: any, completedSteps: { [k: string]: boolean }) => boolean;
}

interface WizardFormProps {
  defaultValues?: any;
  formSteps: FormStep[];
  handleConcluedStep?: (index: number) => void;
  completedSteps: { [k: string]: boolean };
  isLoading?: boolean | null;
  sanitizeEmptyValues?: boolean | undefined;
  transformRecord?: (record: any) => any;
  warnWhenUnsavedChanges?: boolean | undefined;
  /**
   * Estilo das partes, na convenção do MUI. Omitido, cada uma fica como sempre esteve —
   * os outros cinco cadastros que usam este wizard não mudam. A campanha de recuperação
   * passa `form` sem padding e `stepper` como faixa, para a trilha virar parte do cartão.
   */
  slotProps?: {
    root?: SxProps<Theme>;
    form?: SxProps<Theme>;
    stepper?: SxProps<Theme>;
    content?: SxProps<Theme>;
  };
}

const WizardForm: React.FC<WizardFormProps> = ({ formSteps, defaultValues, completedSteps, sanitizeEmptyValues, handleConcluedStep, isLoading, transformRecord, warnWhenUnsavedChanges, slotProps }) => {

  const [index, setIndex] = useState(0);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const notify = useNotify();
  const record = useRecordContext();
  const safeRecord = record || {};

  const nextStep = (value?: number | null) => {
    if (handleConcluedStep) {
      handleConcluedStep(index);
    }

    if (value !== null && value !== undefined) { setIndex(value); }

    if ((formSteps.length - 1) > index) {
      setIndex(index => index + 1);
    }
  }

  const handleSimpleFormButton = async (values: any) => {
    try {
      return await new Promise(async (resolve) => resolve(await (async () => {
        setIsButtonLoading(true);
        const { onNextFunction, validate } = formSteps[index];

        if (validate) {
          const { error, message } = await validate(values);
          if (error && message) {
            notify(message, { type: 'warning' });
            return;
          }
        }

        if (onNextFunction) {
          onNextFunction(values, nextStep);
        } else {
          nextStep();
        }
      })()));
    } finally {
      setIsButtonLoading(false);
    }
  }

  const SimpleFormButton: React.FC<ButtonProps> = ({ ...props }) => (
    <WizardButton
      {...props}
      onClick={(values) => handleSimpleFormButton(values)}
    />
  );

  const ToolbarCustom = (props: any) => (
    <Toolbar>
      <FormDataConsumer>
        {({ formData }) => (
          <Box display="flex" gap={2}>
            <SimpleFormButton
              {...props}
              formData={formData}
              label={(formSteps.length - 1) === index ? 'Salvar' : 'Próximo'}
              startIcon={(formSteps.length - 1) === index && <SaveIcon />}
              variant="contained"
              size="medium"
              isLoading={isButtonLoading || isLoading}
              disabled={formSteps[index].disableToolbarButton || false}
            />
            {/* <MuiButton
              variant="outlined"
              color="secondary"
              size="medium"
              startIcon={<BugReportIcon />}
              onClick={() => {
                console.log('=== DEBUG: Form Context ===');
                console.log('Current Step:', index);
                console.log('Step Label:', formSteps[index].label);
                console.log('Form Data:', formData);
                console.log('Completed Steps:', completedSteps);
                console.log('========================');
              }}
            >
              Debug
            </MuiButton> */}
          </Box>
        )}
      </FormDataConsumer>
    </Toolbar>
  );

  return (
    <Box sx={{ marginTop: '20px', ...(slotProps?.root as object) }}>
      <SimpleForm
        defaultValues={defaultValues || null}
        sx={slotProps?.form}
        toolbar={formSteps[index].disableToolbar !== true && <ToolbarCustom />}
        // TODO: warnWhenUnsavedChanges requires Data Router in React Admin v5 - disabled until router migration
        // warnWhenUnsavedChanges={warnWhenUnsavedChanges}
        sanitizeEmptyValues={sanitizeEmptyValues}
        record={transformRecord ? transformRecord(safeRecord) : safeRecord}
      >
        <FormDataConsumer>
          {({ formData }) =>
            <Stepper sx={{ width: '100%', marginBottom: '30px', ...(slotProps?.stepper as object) }} activeStep={index}>
              {formSteps.map(({ label, disableStep }, idx) => (
                <Step key={label} completed={completedSteps[idx] === true}>
                  <StepButton
                    color="inherit"
                    disabled={(isLoading || (disableStep ? disableStep(formData, completedSteps) : false)) || false}
                    onClick={() => setIndex(idx)}
                  >
                    {label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          }
        </FormDataConsumer>
        <Box sx={{ pl: 1.5, pr: 1.5, width: '100%', ...(slotProps?.content as object) }}>
          <FormDataConsumer>
            {({ formData }) =>
              React.cloneElement(formSteps[index].component, { nextStep, SimpleFormButton, formData })
            }
          </FormDataConsumer>
        </Box>
      </SimpleForm>
    </Box>
  );
}

export default React.memo(WizardForm);